import type { ComponentType} from 'react';
import { useMemo } from 'react';
import { useEventListener } from 'usehooks-ts';
import type { Facet, FacetData, FacetGroup, FacetValue } from '../../model/Facets';
import FacetButton from '../../search/FacetButton';
import SearchFacets from '../../search/SearchFacets';
import removeDiacritics from '../../utils/removeDiacritics';
import { updateSearchParams } from '../../utils/url';
import { toArrayState } from '../../utils/useArrayState';
import useBooleanState from '../../utils/useBooleanState';
import useStateWithSideEffect from '../../utils/useStateWithSideEffect';
import * as style from './FilterableGrid.module.css';
import { Grid } from './Grid';
import type { Tag } from './GridItemTags';
import type { TaggedSanityDocument } from './TaggedGridItem';

interface Props<T extends TaggedSanityDocument> {
    selectedTags?: string[];
    items: T[];
    gridMode?: 'auto-fit' | 'auto-fil' | 'single-column';
    component: ComponentType<T>;
}

function updateTagsInUrl(tags: string[]) {
    const params = new URLSearchParams(location.search);
    params.delete('tag');
    for (const tag of tags) {
        params.append('tag', tag);
    }
    updateSearchParams(params);
}

export function FilterableGrid<T extends TaggedSanityDocument>({ items, selectedTags: initialSelectedTags = [], component, gridMode }: Props<T>) {
    const [showFacetPopover, setShowFacetPopover] = useBooleanState();
    const [selectedTags, setSelectedTags] = toArrayState(useStateWithSideEffect(initialSelectedTags, updateTagsInUrl));

    const knownSelectedTags = useMemo(() => getKnownSelectedTags(items, selectedTags), [items, selectedTags]);
    const filteredList = useMemo(() => filterByTags(items, knownSelectedTags), [items, knownSelectedTags]);
    const facets = useMemo(() => getFacets(filteredList, knownSelectedTags), [filteredList, knownSelectedTags]);

    useEventListener('popstate', () => {
        const tags = new URL(location.href).searchParams.getAll('tag');
        setSelectedTags(tags);
    });

    return (
        <>
            <div className={style.filterableGrid}>
                {facets.any && (
                    <div className={style.mobileFacets}>
                        <FacetButton onClick={setShowFacetPopover.toTrue} title={`Endre filtre (${facets.breadcrumbs.length})`} />
                    </div>
                )}
                <SearchFacets
                    facets={facets}
                    facetsPopover={showFacetPopover}
                    totalResults={filteredList.length}
                    onFacetValueSelect={setSelectedTags.append}
                    onFacetValueUnselect={setSelectedTags.remove}
                    onFacetsCloseClicked={setShowFacetPopover.toFalse}
                    onLocationChange={() => Promise.resolve()}
                />
                <Grid items={filteredList} component={component} gridMode={gridMode} />
            </div>
        </>
    );
}

function getKnownSelectedTags<T extends TaggedSanityDocument>(list: T[], selectedTags: string[]): string[] {
    const allKnownTags = list
        .flatMap((l) => l.tags)
        .filter((t) => t != undefined)
        .filter((t) => t.slug?.current)
        .map((t) => t.slug.current);

    return selectedTags.filter((t) => allKnownTags.includes(t));
}

function filterByTags<T extends TaggedSanityDocument>(list: T[], selectedTags: string[]): T[] {
    if (!selectedTags.length) return list;

    return list.filter((l) => selectedTags.every((s) => l.tags?.some((t) => t.slug.current === s)));
}

function getFacets<T extends TaggedSanityDocument>(list: T[], selectedTags: string[]): FacetData {
    const facetGroups = getTags<T>(list, selectedTags);

    return {
        groupedFacets: facetGroups.filter((g) => !g.facets.flatMap((f) => f.values).some((v) => selectedTags.includes(v.code))),
        any: facetGroups.length > 0,
        breadcrumbs: facetGroups
            .flatMap((g) => g.facets)
            .flatMap((f) =>
                f.values
                    .filter((v) => v.selected)
                    .map((v) => ({
                        facetName: f.name,
                        facetCode: f.code,
                        facetValueName: v.name,
                        facetValueCode: v.code,
                        removeQuery: v.query,
                        priority: 0,
                    })),
            ),
    };
}

const byNameAscending = (a: { name: string }, b: { name: string }): number => a.name.localeCompare(b.name, 'no');
const byPriorityAndNameAscending = (a: { name: string; priority: number }, b: { name: string; priority: number }): number =>
    a.priority - b.priority || a.name.localeCompare(b.name, 'no');

interface TagValue {
    code: string;
    name: string;
    count: number;
    selected: boolean;
    query: string[];
}

function getTags<T extends TaggedSanityDocument>(list: T[], selectedTags: string[]): FacetGroup[] {
    const tags = list
        .flatMap((l) => l.tags)
        .filter((t) => t != undefined)
        .filter((t) => t.slug?.current);

    const groups = Object.entries(Object.groupBy(tags, (t) => t.mainTagCategory?.slug.current ?? 'singleValues')) as [string, Tag[]][];

    const tagGroups = groups.map(([, values]) => ({
        mainTagCategory: values![0].mainTagCategory,
        values: Object.entries(Object.groupBy(values!, (v) => v.slug.current))
            .map(([code, list]) => ({
                code,
                name: list![0].name,
                count: list!.length,
                selected: selectedTags.includes(code),
                query: getQuery(selectedTags, code),
            }))
            .sort(byNameAscending),
    }));

    return tagGroups
        .map(
            ({ mainTagCategory, values }) =>
                ({
                    code: mainTagCategory?.slug.current ?? 'singleValues',
                    name: mainTagCategory?.name ?? 'singleValues',
                    priority: !mainTagCategory ? 2 : 1,
                    expanded: !mainTagCategory,
                    facets: !mainTagCategory
                        ? values.map((value) => makeFacet(value.code, value.name, value))
                        : [makeFacet(mainTagCategory.slug.current, mainTagCategory.name, values)],
                }) satisfies FacetGroup,
        )
        .sort(byPriorityAndNameAscending);
}

function getQuery(selectedTags: string[], code: string) {
    if (selectedTags.includes(code)) {
        return selectedTags.filter((t) => t !== code);
    } else {
        return [...selectedTags, code];
    }
}

function makeFacet(code: string, name: string, values: TagValue[] | TagValue): Facet {
    return {
        code,
        name,
        priority: 0,
        multiSelect: false,
        booleanFacet: !Array.isArray(values),
        values: Array.isArray(values) ? values.map(makeFacetValue) : [makeFacetValue(values)],
    };
}

function makeFacetValue(value: TagValue): FacetValue {
    return {
        ...value,
        query: value.code,
        href: `?${value.query.map((t) => `tag=${t}`).join('&')}`,
        get normalizedName() {
            this.__normalizedName ??= removeDiacritics(value.name);
            return this.__normalizedName;
        },
    };
}
